// CareLogix™ ORACLE — ORACLE PREDICTIVE COMMAND™ page (Phase 33 §2)
// Dedicated route /oracle/predictive. Desktop multi-panel + mobile swipe cards.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PALETTE } from "@/components/animation/MotionShared";
import OracleChartIntelligence from "@/components/predictive/OracleChartIntelligence";
import OraclePredictiveChart   from "@/components/predictive/OraclePredictiveChart";
import OraclePatternScanner    from "@/components/predictive/OraclePatternScanner";
import OracleSignalReplay      from "@/components/predictive/OracleSignalReplay";
import OracleConfidenceBand    from "@/components/predictive/OracleConfidenceBand";
import AgentSwarmActivityPanel from "@/components/swarm/AgentSwarmActivityPanel";
import SignalPulse             from "@/components/animated/SignalPulse";
import ComplianceLock          from "@/components/compliance/ComplianceLock";
import { useMembership }       from "@/components/membership/MembershipContext";

const SYMBOLS_DEFAULT = ["AAPL", "MSFT", "NVDA", "GOOGL", "AMZN", "META", "TSLA", "AMD"];
const DOMAINS = [
  { v: "stocks",       l: "Stocks" },
  { v: "etfs",         l: "ETFs" },
  { v: "crypto",       l: "Crypto" },
  { v: "forex",        l: "Forex" },
  { v: "healthcare",   l: "Healthcare" },
  { v: "startups",     l: "Startups" },
  { v: "global_macro", l: "Global Macro" },
];

function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 820px)");
    const fn = () => setM(mq.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);
  return m;
}

export default function OraclePredictiveCommand() {
  const { tier, tierLabel } = useMembership();
  const [symbol, setSymbol] = useState("AAPL");
  const [domain, setDomain] = useState("stocks");
  const [mobileIdx, setMobileIdx] = useState(0);
  const isMobile = useIsMobile();
  const symbols = useMemo(() => SYMBOLS_DEFAULT, []);
  const isFree  = tier === "free";

  const panels = [
    <OracleChartIntelligence key="chart" symbol={symbol} domain={domain} testid="predictive-chart-intel" />,
    <OraclePredictiveChart   key="pred"  symbol={symbol} domain={domain} testid="predictive-pred-chart" />,
    <OracleConfidenceBand    key="conf"  symbol={symbol} domain={domain} testid="predictive-conf-band" />,
    <OraclePatternScanner    key="pat"   symbols={symbols} domain={domain} testid="predictive-pattern-scanner" />,
    <OracleSignalReplay      key="rep"   symbol={symbol} testid="predictive-signal-replay" />,
  ];

  return (
    <main style={S.shell} data-testid="oracle-predictive-command">
      <div style={S.bgGlow} aria-hidden="true" />
      <header style={S.header}>
        <div style={S.headerLeft}>
          <Link to="/" style={S.backLink} data-testid="predictive-back">← ORACLE</Link>
          <div>
            <div style={S.eyebrow}>PHASE 33 · PREDICTIVE INTELLIGENCE</div>
            <h1 style={S.title} data-testid="predictive-title">ORACLE PREDICTIVE COMMAND™</h1>
            <p style={S.subtitle}>Live predictive intelligence · charts, patterns, confidence, signal replay.</p>
          </div>
        </div>
        <div style={S.tierRow}>
          <span style={S.tierPill} data-testid="predictive-tier-pill">TIER: {tierLabel || tier}</span>
          {isFree && <Link to="/access" style={S.upgradeLink} data-testid="predictive-upgrade">Upgrade for full depth →</Link>}
        </div>
      </header>

      <section style={S.controlsRow} data-testid="predictive-controls">
        <label style={S.label}>
          Symbol
          <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase().slice(0, 8))} style={S.input} data-testid="predictive-symbol-input" />
        </label>
        <label style={S.label}>
          Domain
          <select value={domain} onChange={(e) => setDomain(e.target.value)} style={S.input} data-testid="predictive-domain-select">
            {DOMAINS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
        </label>
        <div style={S.symbolChips}>
          {symbols.map((s) => (
            <button key={s} type="button" onClick={() => setSymbol(s)} style={{ ...S.chip, ...(s === symbol ? S.chipActive : null) }} data-testid={`predictive-chip-${s}`}>{s}</button>
          ))}
        </div>
      </section>

      <SignalPulse testid="predictive-signal-pulse" />

      {isMobile ? (
        <section style={S.mobileWrap} data-testid="predictive-mobile-deck">
          <div style={S.mobileTrack} data-testid="predictive-mobile-track">
            {panels[mobileIdx]}
          </div>
          <div style={S.mobileNav}>
            {panels.map((_, i) => (
              <button key={i} type="button" onClick={() => setMobileIdx(i)} style={{ ...S.mobileDot, background: i === mobileIdx ? PALETTE.gold : "rgba(255,255,255,0.15)" }} aria-label={`Panel ${i + 1}`} data-testid={`predictive-mobile-dot-${i}`} />
            ))}
          </div>
        </section>
      ) : (
        <section style={S.desktopGrid} data-testid="predictive-desktop-grid">
          <div style={S.colLeft}>{panels[0]}{panels[1]}{panels[3]}</div>
          <div style={S.colRight}>{panels[2]}{panels[4]}<AgentSwarmActivityPanel testid="predictive-swarm-panel" /></div>
        </section>
      )}

      {isMobile && <AgentSwarmActivityPanel testid="predictive-swarm-panel-mobile" />}

      <ComplianceLock variant="footer" testid="predictive-compliance-lock" />
    </main>
  );
}

const S = {
  shell:    { position: "relative", minHeight: "100vh", padding: "20px clamp(12px, 3vw, 28px) 60px", background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.45) 0%, rgba(4,6,8,1) 70%)", color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  bgGlow:   { position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse 500px 280px at 50% 0%, rgba(197,160,89,0.10), transparent 65%)", pointerEvents: "none" },
  header:   { position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  headerLeft:{ display: "flex", gap: 16, alignItems: "flex-start" },
  backLink: { color: PALETTE.gold, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 12, padding: "6px 12px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 999 },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.24em" },
  title:    { margin: "4px 0 0", color: PALETTE.goldBright, fontSize: "clamp(20px, 3.2vw, 28px)", letterSpacing: "0.08em", fontWeight: 800 },
  subtitle: { margin: "4px 0 0", color: PALETTE.slate4, fontSize: 13 },
  tierRow:  { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 },
  tierPill: { fontFamily: "'Space Mono', monospace", color: PALETTE.goldBright, fontSize: 11, padding: "4px 10px", border: `1px solid ${PALETTE.gold}`, borderRadius: 999 },
  upgradeLink:{ color: PALETTE.gold, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  controlsRow:{ position: "relative", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" },
  label:    { display: "flex", flexDirection: "column", gap: 4, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.08em" },
  input:    { background: "#040608", color: PALETTE.slate3, border: `1px solid ${PALETTE.goldGlow}`, padding: "7px 10px", borderRadius: 8, fontSize: 13, minWidth: 120 },
  symbolChips:{ display: "flex", flexWrap: "wrap", gap: 4, marginLeft: "auto" },
  chip:     { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, padding: "4px 10px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  chipActive:{ background: PALETTE.gold, color: PALETTE.bgDeep, borderColor: PALETTE.gold, fontWeight: 800 },
  desktopGrid:{ position: "relative", display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", gap: 14 },
  colLeft:  { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
  colRight: { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
  mobileWrap:{ position: "relative", display: "flex", flexDirection: "column", gap: 8 },
  mobileTrack:{ minHeight: 280 },
  mobileNav:{ display: "flex", gap: 6, justifyContent: "center" },
  mobileDot:{ width: 22, height: 6, borderRadius: 3, border: "none", cursor: "pointer" },
};
